import { DRAGON } from '@somnhold/content';
import { hexLen, worldToHex } from '../hex.ts';
import { cosA, iatan2, idiv, imax, imin, isqrt, mulBp, sinA, withinRange } from '../math/fixed.ts';
import { rngInt } from '../math/rng.ts';
import type { BattleState, Enemy, Hero, PlayerId } from '../types.ts';
import { damageEnemy, damageHero, type DamageOpts, ev, spawnEnemy } from './combat.ts';
import { chaseHero } from './dungeon.ts';
import { stepToward } from './enemies.ts';

/**
 * The dragon of the Burn (docs/UNICAS.md §4). It is fought in a clearing of its own with no node: it circles in the sky
 * burning lines of ground, and the old harpoons on the rim bring it down (two stuck at once); alone it lands after a
 * while, roaring up inked nightmares. On the ground it bites, sweeps and eats the absent friends' spectres. At 55 % it
 * goes up once more; from 30 % (or past the clock) it rages, and the clearing turns to glass from the rim in.
 *
 * Its six glass scales let little through. Frost fills the glass's cold; full, the next dry blow (a critical, a stun,
 * lightning, a turret's bolt, a harpoon, Orla's arrows) breaks a scale. Fire does nothing to it but close broken ones.
 * Brought down, the glass is brittle: a dry blow breaks one a second without any cold.
 */

const D = DRAGON;
/** A full turn of its circle, in its own steps. */
const TURN = 8192;
/** Eaten: down for good (no respawn reaches it). */
export const EATEN = 0x3fffffff;

type DragonSt = NonNullable<BattleState['dragon']>;

const standing = (s: BattleState): Hero[] => s.heroes.filter((h) => h.downTicks === 0);

/** What gets through its scales now (bp). */
export const scaleBp = (st: DragonSt): number => imin(10000, D.scales.wholeBp + D.scales.perBp * st.broken);

/** Brittle: the cold full, or brought down. */
export const brittle = (s: BattleState): boolean => {
  const st = s.dragon;
  const r = s.rules?.dragon;
  return !!st && !!r && (st.phase === 'down' || st.cold >= r.ask);
};

/** A burning mark on the ground, bursting after its warning (La Quemadura's map warns it longer). */
const mark = (s: BattleState, x: number, y: number, radius: number, warn0: number, damage: number): void => {
  const warn = mulBp(warn0, s.rules?.dragon?.warnBp ?? 10000);
  s.zones.push({ id: s.nextId++, kind: 'eruption', x, y, radius, cells: [], ticks: warn + 12, every: 1, damage, slowBp: 0, pid: '', warn });
};

/** Fire along a line from the dragon toward a hero. */
const breath = (s: BattleState, e: Enemy, h: Hero): void => {
  const b = D.breath;
  const dx = h.x - e.x;
  const dy = h.y - e.y;
  const d = isqrt(dx * dx + dy * dy) || 1;
  for (let i = 1; i <= b.marks; i++) mark(s, e.x + idiv(dx * b.spacing * i, d), e.y + idiv(dy * b.spacing * i, d), b.radius, b.warn, b.damage);
  ev(s, { t: 'skill', hero: e.id, skill: 'dragonBreath', x: h.x, y: h.y });
};

/** Its roar: inked nightmares, by the heroes counted (a spectre is part of one; the cast are none). */
const roar = (s: BattleState, e: Enemy): void => {
  const n0 = idiv(s.rules?.dragon?.heroesBp ?? 10000, 10000);
  const n = D.roarAdds[n0 >= D.roarAdds.length ? D.roarAdds.length - 1 : n0] ?? 3;
  for (let i = 0; i < n; i++) {
    const a = idiv(i * 1024, n);
    const x = e.x + idiv(cosA(a) * 1600, 10000);
    const y = e.y + idiv(sinA(a) * 1600, 10000);
    const add = spawnEnemy(s, i % 2 ? 'wolf' : 'wraith', 2, false, 0, x, y, 0);
    if (s.rules?.enemyHpBp) add.hp = add.maxHp = imax(1, mulBp(add.maxHp, s.rules.enemyHpBp));
    ev(s, { t: 'spawn', id: add.id });
  }
  ev(s, { t: 'skill', hero: e.id, skill: 'dragonRoar', x: e.x, y: e.y });
};

/** In the beacon's light (La Quemadura 4). */
const lit = (s: BattleState, x: number, y: number): boolean => {
  const r = s.rules?.dragon?.beaconR ?? 0;
  return r > 0 && withinRange(x, y, 0, 0, r);
};

/**
 * Eats a marked spectre near it (an absent friend's; never a live hero, nor Gaspar, Maelis or Orla), mending itself.
 * Eaten is not fallen: out of the fight for good, without passing through `heroDown` (nothing holds it back).
 */
const devour = (s: BattleState, e: Enemy, st: DragonSt): boolean => {
  const marked = s.rules?.dragon?.spectres ?? [];
  const g = standing(s).find((h) => h.control === 'ghost' && marked.includes(h.pid) && !st.eaten.includes(h.pid) && withinRange(e.x, e.y, h.x, h.y, D.devour.range) && !lit(s, h.x, h.y));
  if (!g) return false;
  st.eaten.push(g.pid);
  g.hp = 0;
  g.downTicks = EATEN;
  g.target = 0;
  g.moving = false;
  g.path = [];
  g.anim = 'down';
  g.animT = 0;
  e.hp = imin(e.maxHp, e.hp + mulBp(e.maxHp, D.devour.healBp));
  ev(s, { t: 'down', id: g.id });
  ev(s, { t: 'skill', hero: e.id, skill: 'dragonDevour', x: g.x, y: g.y });
  return true;
};

/** Whether a hero was eaten by the dragon this fight. */
export const eaten = (s: BattleState, pid: PlayerId): boolean => !!s.dragon?.eaten.includes(pid);

/* ---------- the scales ---------- */

const breakScale = (s: BattleState, e: Enemy, st: DragonSt): void => {
  if (st.broken >= D.scales.n) return;
  st.broken++;
  st.broke++;
  st.cold = 0;
  ev(s, { t: 'boom', x: e.x, y: e.y, r: 900, kind: 'dragonScale' });
};

const closeScale = (s: BattleState, e: Enemy, st: DragonSt, fixed: number): boolean => {
  if (st.broken <= fixed) return false;
  st.broken--;
  ev(s, { t: 'boom', x: e.x, y: e.y, r: 900, kind: 'dragonClose' });
  return true;
};

/** A dry blow: brittle, it breaks a scale (brought down, one a second at most). */
const dryBlow = (s: BattleState, e: Enemy, st: DragonSt): void => {
  const r = s.rules?.dragon;
  if (!r) return;
  if (st.phase === 'down') {
    if (s.tick < st.breakAt) return;
    st.breakAt = s.tick + D.down.every;
    breakScale(s, e, st);
  } else if (st.cold >= r.ask) breakScale(s, e, st);
};

/**
 * A blow on the dragon (from `damageEnemy`, before anything else): frost fills the cold, a dry blow may break a scale,
 * fire closes them and does nothing else. Returns what is left of it through the scales (0: nothing lands).
 */
export const dragonTakes = (s: BattleState, e: Enemy, amount: number, o: DamageOpts): number => {
  const st = s.dragon;
  const r = s.rules?.dragon;
  if (!st || !r || e.id !== st.id) return amount;
  const type = o.type ?? 'physical';
  if (type === 'fire') {
    // A burn on it (the gear's, an enchanted bolt's) never reaches it and doesn't count: only fire someone throws.
    if (o.dot) return 0;
    st.fireAll += amount;
    // Nothing broken to close: the fire is spent on whole glass (none of it waits for the next scale).
    if (st.broken <= r.fixed) {
      st.fire = 0;
      return 0;
    }
    st.fire += amount;
    while (st.fire >= r.close) {
      if (!closeScale(s, e, st, r.fixed)) {
        st.fire = 0;
        break;
      }
      st.fire -= r.close;
      st.closed++;
    }
    return 0;
  }
  if (type === 'frost') {
    // The shards of a shatter are frost too, but they never feed the glass (or it would feed itself).
    if (!o.shard && !o.dot) {
      st.cold = imin(r.ask, st.cold + amount);
      st.coldAt = s.tick;
    }
  } else if (!o.dot && (o.crit || o.harpoon || o.bolt || type === 'lightning' || o.pid === 'npc:orla')) dryBlow(s, e, st);
  return mulBp(amount, scaleBp(st));
};

/* ---------- the sky, the harpoons, the fall ---------- */

/** Steps along its circle from `a` forward to `b`. */
const ahead = (a: number, b: number): number => (((b - a) % TURN) + TURN) % TURN;

/** Half a lane's width as steps of its circle. */
const HALF_LANE = idiv(idiv(D.harpoons.lane, 2) * TURN * 1000, D.orbit * 6283);

/** Its steps a tick in the sky: a harpoon stuck in it halves them (nothing else slows it up there). */
const pace = (s: BattleState, st: DragonSt): number => (st.stuck.some((t) => t > s.tick) ? mulBp(D.arc, D.harpoons.slowBp) : D.arc);

/** Whether a harpoon's lane is lit: loaded, with the dragon in the sky about to cross it (`warn` ahead) or over it now. */
export const laneLit = (s: BattleState, i: number): boolean => {
  const st = s.dragon;
  const h = s.rules?.dragon?.harpoons[i];
  if (!st || !h || st.phase !== 'sky' || (st.ready[i] ?? 0) > s.tick) return false;
  const d = ahead(st.arc, h.arc);
  if (d <= HALF_LANE || d >= TURN - HALF_LANE) return true;
  return idiv(d - HALF_LANE, imax(1, pace(s, st))) <= D.harpoons.warn;
};

/** Ticks until the dragon crosses a lit lane (0: over it now; -1: not lit), for the screen's warning. */
export const laneIn = (s: BattleState, i: number): number => {
  const st = s.dragon;
  const h = s.rules?.dragon?.harpoons[i];
  if (!st || !h || !laneLit(s, i)) return -1;
  const d = ahead(st.arc, h.arc);
  return d <= HALF_LANE || d >= TURN - HALF_LANE ? 0 : idiv(d - HALF_LANE, imax(1, pace(s, st)));
};

/** Harpoons stuck in it now. */
export const stuckNow = (s: BattleState): number => (s.dragon ? s.dragon.stuck.filter((t) => t > s.tick).length : 0);

const knockDown = (s: BattleState, e: Enemy, st: DragonSt): void => {
  st.phase = 'down';
  st.since = s.tick;
  st.downs++;
  st.stuck = st.stuck.map(() => 0);
  st.breakAt = s.tick + D.down.every;
  e.air = false;
  e.anim = 'stun';
  e.animT = 0;
  for (let k = 0; k < D.down.breaks; k++) breakScale(s, e, st);
  ev(s, { t: 'boom', x: e.x, y: e.y, r: 2600, kind: 'dragonDown' });
};

const fire = (s: BattleState, e: Enemy, st: DragonSt, i: number, by: Hero): void => {
  const h = (s.rules?.dragon?.harpoons ?? [])[i];
  if (!h) return;
  st.ready[i] = s.tick + D.harpoons.reload;
  st.stuck[i] = s.tick + D.harpoons.hold;
  st.by[i] = by.pid;
  st.lever[i] = 0;
  ev(s, { t: 'shot', from: by.id, to: e.id, kind: 'harpoon', x: h.x, y: h.y, tx: e.x, ty: e.y });
  damageEnemy(s, e, imax(1, mulBp(e.maxHp, D.harpoons.hitBp)), { pid: by.pid, ignoresArmor: true, noReact: true, harpoon: true });
  // Two at once bring it down, whatever the party.
  if (st.phase === 'sky' && stuckNow(s) >= 2) knockDown(s, e, st);
};

const harpoons = (s: BattleState, e: Enemy, st: DragonSt): void => {
  const list = s.rules?.dragon?.harpoons ?? [];
  // The first one is always someone live's (never a spectre nor the cast, Orla included): §4.11.
  const first = stuckNow(s) === 0;
  const up = standing(s).filter((o) => !first || (o.control !== 'ghost' && !o.pid.startsWith('npc:')));
  for (let i = 0; i < list.length; i++) {
    const h = list[i] as (typeof list)[number];
    const at = laneLit(s, i) ? up.find((o) => withinRange(o.x, o.y, h.lx, h.ly, D.harpoons.reach)) : undefined;
    if (!at) {
      st.lever[i] = 0;
      continue;
    }
    st.lever[i] = (st.lever[i] ?? 0) + 1;
    if ((st.lever[i] ?? 0) >= D.harpoons.lever) {
      fire(s, e, st, i, at);
      if (st.phase !== 'sky') return;
    }
  }
};

/** Up into the sky again (at 55 %, once): every scale but the ones that never close closes. */
const rise = (s: BattleState, e: Enemy, st: DragonSt): void => {
  st.phase = 'sky';
  st.since = s.tick;
  st.flights++;
  st.rose = true;
  st.broken = imin(st.broken, s.rules?.dragon?.fixed ?? 0);
  st.cold = 0;
  st.arc = iatan2(e.y, e.x) * 8;
  st.lever = st.lever.map(() => 0);
  e.air = true;
  e.timer = D.every[0] as number;
  ev(s, { t: 'boom', x: e.x, y: e.y, r: 2400, kind: 'dragonRise' });
};

/** Down by itself (out of the sky's time): whole, roaring. */
const land = (s: BattleState, e: Enemy, st: DragonSt): void => {
  st.phase = 'ground';
  st.since = s.tick;
  st.stuck = st.stuck.map(() => 0);
  e.air = false;
  e.timer = D.every[1] as number;
  ev(s, { t: 'boom', x: e.x, y: e.y, r: 2400, kind: 'dragonLand' });
  roar(s, e);
};

/** The time the glass takes to come in to ring `ring` (double inside the beacon's light). */
const glassStep = (s: BattleState, ring: number): number => (lit(s, ring * 1000, 0) ? mulBp(D.fury.every, D.fury.beaconBp) : D.fury.every);

const startFury = (s: BattleState, e: Enemy, st: DragonSt): void => {
  const r = s.rules?.dragon;
  st.phase = 'fury';
  st.since = s.tick;
  st.fury = false;
  st.furyAt = s.tick;
  st.stuck = st.stuck.map(() => 0);
  // The rim is glass at once; from there it comes in a hex every `every` (about three minutes to the middle, §4.4).
  st.glass = r?.radius ?? D.field.radius;
  st.glassAt = s.tick + glassStep(s, st.glass);
  st.closeAt = s.tick + D.fury.close;
  e.air = false;
  e.speed = mulBp(e.speed, D.furySpeedBp);
  e.timer = D.every[2] as number;
  ev(s, { t: 'boom', x: e.x, y: e.y, r: 2400, kind: 'dragonFury' });
  roar(s, e);
};

/** Whether a point is on the burning glass of its fury (outside the safe ground). */
export const onGlass = (s: BattleState, x: number, y: number): boolean => {
  const st = s.dragon;
  if (!st || st.phase !== 'fury') return false;
  const h = worldToHex(x, y);
  return hexLen(h.q, h.r) > st.glass;
};

/** The fury's clock: the rim comes in, the heat closes scales, and the glass burns whoever stands on it. */
const furyTick = (s: BattleState, e: Enemy, st: DragonSt): void => {
  const r = s.rules?.dragon;
  if (!r) return;
  if (s.tick >= st.glassAt) {
    st.glass--;
    st.glassAt = s.tick + glassStep(s, st.glass);
    ev(s, { t: 'boom', x: 0, y: 0, r: imax(0, st.glass) * 1000, kind: 'dragonGlass' });
  }
  if (s.tick >= st.closeAt) {
    st.closeAt = s.tick + D.fury.close;
    closeScale(s, e, st, r.fixed);
  }
  if ((s.tick - st.furyAt) % D.fury.pulse === 0) for (const h of standing(s)) if (onGlass(s, h.x, h.y)) damageHero(s, h, D.fury.damage, null, 'fire', true);
  // The glass covers the clearing: nowhere left to stand.
  if (st.glass < 0 || s.tick - st.furyAt >= D.fury.max) st.lost = 'glass';
};

/* ---------- the tick ---------- */

const init = (s: BattleState, e: Enemy): DragonSt => {
  const r = s.rules?.dragon;
  const n = r?.harpoons.length ?? 0;
  const st: DragonSt = {
    id: e.id,
    arc: iatan2(e.y, e.x) * 8,
    phase: 'sky',
    since: s.tick,
    flights: 1,
    rose: false,
    broken: r?.fixed ?? 0,
    cold: 0,
    coldAt: s.tick,
    fire: 0,
    breakAt: 0,
    dry: false,
    fury: false,
    lever: Array.from({ length: n }, () => 0),
    ready: Array.from({ length: n }, () => 0),
    stuck: Array.from({ length: n }, () => 0),
    by: Array.from({ length: n }, () => ''),
    furyAt: 0,
    glass: (r?.radius ?? D.field.radius) + 1,
    glassAt: 0,
    closeAt: 0,
    eaten: [],
    low: false,
    fireAll: 0,
    closed: 0,
    broke: 0,
    downs: 0,
    lost: '',
  };
  s.dragon = st;
  e.air = true;
  e.timer = D.every[0] as number;
  return st;
};

export const dragonTick = (s: BattleState, e: Enemy, movePx: (e: Enemy) => number, attack: (h: Hero) => void): void => {
  const st = s.dragon && s.dragon.id === e.id ? s.dragon : init(s, e);
  const r = s.rules?.dragon;
  if (!r) return;
  // A stun that landed on it this tick: it never stuns, but it was a dry blow.
  if (e.dry) {
    e.dry = undefined;
    dryBlow(s, e, st);
  }
  // The cold drops when no frost comes for a while.
  if (st.cold > 0 && s.tick - st.coldAt > D.scales.still && (s.tick - st.coldAt) % 12 === 0) st.cold = imax(0, st.cold - mulBp(r.ask, D.scales.dropBp));
  const hpBp = idiv(e.hp * 10000, e.maxHp || 1);
  // Its life under 30 % (whatever its fury): a scar for the tries after this one.
  if (hpBp < D.furyAtBp) st.low = true;
  // Under 55 %: up once more from the ground; in the sky or brought down it is spared.
  if (!st.rose && hpBp <= D.riseAtBp) {
    if (st.phase === 'ground') rise(s, e, st);
    else st.rose = true;
  }
  // Under 30 %, or past the clock: its fury (straight down out of the sky; after the fall, once it is over).
  if (st.phase !== 'fury' && (hpBp <= D.furyAtBp || s.waveT >= D.clock)) {
    if (st.phase === 'down') st.fury = true;
    else startFury(s, e, st);
  }
  const heroes = standing(s);
  if (st.phase === 'down') {
    e.anim = 'stun';
    if (s.tick - st.since >= D.down.ticks) {
      if (st.fury) startFury(s, e, st);
      else {
        // Up off the ground, on its feet: no roar (being brought down cost it that).
        st.phase = 'ground';
        st.since = s.tick;
        e.timer = D.every[1] as number;
        e.anim = 'walk';
      }
    }
    return;
  }
  if (st.phase === 'sky') {
    if (s.tick - st.since >= (st.flights > 1 ? D.sky.second : D.sky.first)) {
      land(s, e, st);
      return;
    }
    harpoons(s, e, st);
    if (st.phase !== 'sky') return;
    st.arc = (st.arc + pace(s, st)) % TURN;
    e.orbit = st.arc >> 3;
    stepToward(e, idiv(cosA(e.orbit) * D.orbit, 10000), idiv(sinA(e.orbit) * D.orbit, 10000), D.orbit);
    e.anim = 'walk';
    if (--e.timer <= 0 && heroes.length) {
      e.timer = D.every[0] as number;
      breath(s, e, heroes[rngInt(s.rng, heroes.length)] as Hero);
    }
    return;
  }
  if (st.phase === 'fury') furyTick(s, e, st);
  if (--e.timer <= 0 && heroes.length) {
    const fury = st.phase === 'fury';
    e.timer = D.every[fury ? 2 : 1] as number;
    const target = heroes[rngInt(s.rng, heroes.length)] as Hero;
    // A spectre within reach is eaten first; else its tail, or its fire.
    if (!devour(s, e, st)) {
      if (!fury && heroes.some((h) => withinRange(e.x, e.y, h.x, h.y, D.sweep.radius))) {
        mark(s, e.x, e.y, D.sweep.radius, D.sweep.warn, D.sweep.damage);
        ev(s, { t: 'skill', hero: e.id, skill: 'dragonSweep', x: e.x, y: e.y });
      } else breath(s, e, target);
    }
    if (fury && D.furyEruptions) for (const h of heroes) mark(s, h.x, h.y, D.breath.radius, D.breath.warn, D.breath.damage);
  }
  chaseHero(s, e, movePx, (h) => {
    attack(h);
    // Its bite catches whoever stands beside its target.
    if (e.cd === e.reload) for (const o of standing(s)) if (o !== h && withinRange(o.x, o.y, h.x, h.y, D.cleaveRadius)) damageHero(s, o, idiv(e.damage, 2));
  });
};

/** The fight is over: the dragon felled wins it; everyone down at once, or the glass over the clearing, loses it. */
export const dragonResult = (s: BattleState): 'won' | 'lost' | null => {
  if (s.bossId && !s.enemies.some((e) => e.id === s.bossId && e.hp > 0)) return 'won';
  const st = s.dragon;
  if (!st) return null;
  if (st.lost === 'glass') return 'lost';
  // The players' heroes (live or spectres): the cast standing doesn't hold the fight (Gaspar, Maelis, Orla).
  const players = s.heroes.filter((h) => !h.pid.startsWith('npc:'));
  if (players.length && players.every((h) => h.downTicks > 0)) {
    st.lost = 'fallen';
    return 'lost';
  }
  return null;
};
